import type { SurveyQuestion } from '@/modules/survey';

// A SurveyJS element as returned in `DynamicUiPayload.elements`.
// Typed loosely — only the fields we actually consume are required.
// Choice values are numbers on scale questions (PHQ/GAD send 0–3) and strings
// elsewhere ('yes', 'type_1', …).
type SurveyJsChoice = string | { value?: string | number; text?: string; imageLink?: string };

type SurveyJsElement = {
  type: string;
  name: string;
  title?: string;
  /** SurveyJS helper text under the title (the legacy screeners' `additional_info`). */
  description?: string;
  /** Present on `text` elements: `"date"`, `"email"`, etc. */
  inputType?: string;
  choices?: SurveyJsChoice[];
  /** SurveyJS required flag. Explicitly false → the question is skippable. */
  isRequired?: boolean;
};

function isSurveyJsElement(v: unknown): v is SurveyJsElement {
  return typeof v === 'object' && v !== null && typeof (v as SurveyJsElement).name === 'string';
}

/**
 * Stringify a choice value, preserving `0` (`String(0)` → `'0'`) — a plain
 * truthiness or `??` chain would drop it. `'0'` is a truthy string, so it also
 * survives `useSurveyQuestions`' `surveyChoiceUrn ? urn : order` fallback,
 * which previously replaced value 0 with its order and both corrupted PHQ/GAD
 * scores and produced duplicate React keys against numeric value 1.
 * Stringifying is safe server-side: numeric answers are only consumed through
 * `int(value)` coercion (accumulators, phq_gad_scoring, legacy_sync), and
 * equality predicates only compare string answer values.
 */
function toValueString(v: string | number | undefined): string | undefined {
  return v === undefined ? undefined : String(v);
}

function resolveChoiceText(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return c.text ?? toValueString(c.value) ?? '';
}

function resolveChoiceValue(c: SurveyJsChoice): string {
  if (typeof c === 'string') return c;
  return toValueString(c.value) ?? c.text ?? '';
}

/**
 * Converts the raw SurveyJS `elements` array from a `DynamicUiPayload` into the
 * `SurveyQuestion[]` shape that `SurveyContent` / `useSurveyQuestions` consume.
 *
 * Key mapping:
 *   element.name          → question.key   (the signal key, e.g. `taking_aoms`)
 *   element.title         → question.text  (shown as the page heading)
 *   element.description   → question.additionalInfo (the heading subtitle — the
 *                           legacy screeners' `additional_info`: lab_preference's
 *                           in-network cost note, the AOM refill "best estimate"
 *                           reassurance, every pre-diabetes question's explainer)
 *   choice.value          → surveyChoiceUrn (used by useSurveyQuestions as the
 *                           submitted answer value — no URN needed here;
 *                           numeric values are stringified, see toValueString)
 *   choice.imageLink      → imageLink       (optional per-choice icon URL, e.g.
 *                           health goals; rendered as a leading avatar when set)
 *   type: "checkbox"      → isMultiSelect: true (SurveyContent renders checkbox
 *                           rows + submits a string[]); radiogroup → single-select
 *   inputType: "date"     → questionType: "date" (SurveyContent renders date input)
 */
export function toDynamicSurveyQuestions(
  elements: unknown[],
  /** Page-level title fallback when the element has no own title (e.g. refill date). */
  pageTitle?: string | null,
  /** Page-level description fallback when the element has no own description. */
  pageDescription?: string | null
): SurveyQuestion[] {
  return elements.filter(isSurveyJsElement).map((element, index) => ({
    key: element.name,
    order: index + 1,
    text: element.title ?? pageTitle ?? '',
    // Rendered as the ScreenHeading subtitle. `undefined` (not '') when neither
    // level carries one, so SurveyContent omits the subtitle node entirely.
    additionalInfo: element.description ?? pageDescription ?? undefined,
    questionType: element.inputType === 'date' ? 'date' : 'choice',
    // Passed through as-is: only an explicit `false` makes the question
    // skippable in SurveyContent; absent keeps the required default.
    isRequired: element.isRequired,
    // SurveyJS `checkbox` → multi-select; `radiogroup` (and everything else) →
    // single-select. Drives checkbox-vs-radio rendering + array-vs-string answer.
    isMultiSelect: element.type === 'checkbox',
    choices: (element.choices ?? []).map((c, choiceIndex) => ({
      text: resolveChoiceText(c),
      order: choiceIndex + 1,
      surveyChoiceUrn: resolveChoiceValue(c),
      imageLink: typeof c === 'string' ? undefined : c.imageLink,
    })),
  }));
}
